import { useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

const emptyPatientForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  birthDate: ""
}

export default function usePatientFormActions({
  navigate,
  safeClinicId,
  selectedPatientId,
  selectedPatient,
  isReadOnly,
  readOnlyReason,
  query,
  loadPatients
}) {
  const [patientFormOpen, setPatientFormOpen] = useState(false)
  const [patientFormMode, setPatientFormMode] = useState("create")
  const [patientForm, setPatientForm] = useState(emptyPatientForm)
  const [savingPatientForm, setSavingPatientForm] = useState(false)

  const openCreatePatientDrawer = () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura")
      return
    }

    if (!safeClinicId) {
      alert("Selecciona una clínica antes de crear pacientes.")
      return
    }

    setPatientFormMode("create")
    setPatientForm(emptyPatientForm)
    setPatientFormOpen(true)
  }

  const openEditPatientDrawer = () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura")
      return
    }

    if (!selectedPatientId || !selectedPatient) {
      alert("Selecciona un paciente para editar.")
      return
    }

    setPatientFormMode("edit")
    setPatientForm({
      firstName: selectedPatient.firstName || "",
      lastName: selectedPatient.lastName || "",
      phone: selectedPatient.phone || "",
      email: selectedPatient.email || "",
      birthDate: selectedPatient.birthDate
        ? new Date(selectedPatient.birthDate).toISOString().slice(0, 10)
        : ""
    })
    setPatientFormOpen(true)
  }

  const submitPatientForm = async () => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason || "Modo solo lectura")
        return
      }

      if (!safeClinicId) {
        alert("No hay una clínica válida seleccionada.")
        return
      }

      if (!patientForm.firstName.trim()) {
        alert("Escribe el nombre.")
        return
      }

      if (!patientForm.lastName.trim()) {
        alert("Escribe el apellido.")
        return
      }

      setSavingPatientForm(true)

      const payload = {
        firstName: patientForm.firstName.trim(),
        lastName: patientForm.lastName.trim(),
        phone: patientForm.phone?.trim() || undefined,
        email: patientForm.email?.trim() || undefined,
        birthDate: patientForm.birthDate || undefined
      }

      const isEdit = patientFormMode === "edit" && selectedPatientId
      const url = isEdit
        ? `${API_BASE}/patients/${selectedPatientId}`
        : `${API_BASE}/patients`

      const res = await apiFetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo guardar el paciente.")
        return
      }

      await loadPatients(query)

      const newPatientId = isEdit ? selectedPatientId : data?.id
      if (newPatientId) {
        const params = new URLSearchParams()
        if (safeClinicId) params.set("clinicId", safeClinicId)
        navigate(
          `/patients/${newPatientId}${
            params.toString() ? `?${params.toString()}` : ""
          }`
        )
      }

      setPatientFormOpen(false)
      setPatientForm(emptyPatientForm)

      alert(
        isEdit
          ? "Paciente actualizado correctamente."
          : "Paciente creado correctamente."
      )
    } catch {
      alert("Error al guardar paciente.")
    } finally {
      setSavingPatientForm(false)
    }
  }

  return {
    patientFormOpen,
    setPatientFormOpen,
    patientFormMode,
    patientForm,
    setPatientForm,
    savingPatientForm,
    openCreatePatientDrawer,
    openEditPatientDrawer,
    submitPatientForm
  }
}
